'use client';

import React, { useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { stripHtml } from 'string-strip-html';

interface FaqItem {
    id?: number | string;
    question?: string;
    faq_question?: string;
    answer?: string;
    faq_answer?: string;
}

interface FaqAccordionSectionProps {
    title: string;
    subtitle?: string;
    faqs: FaqItem[];
}

const FaqAccordionSection = ({ title, subtitle, faqs }: FaqAccordionSectionProps) => {
    const [openIdx, setOpenIdx] = useState<number | null>(0);

    if (!faqs || faqs.length === 0) return null;

    const safeFaqs = faqs.map((faq, index) => ({
        id: faq.id || index,
        question: stripHtml(faq.question || faq.faq_question || '', {
            stripTogetherWithTheirContents: ['script', 'style', 'noscript', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'form'],
            dumpLinkHrefsNearby: { enabled: false },
        }).result.trim(),
        answer: stripHtml(faq.answer || faq.faq_answer || '', {
            stripTogetherWithTheirContents: ['script', 'style', 'noscript', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'form'],
            dumpLinkHrefsNearby: { enabled: false },
        }).result.trim(),
    })).filter((faq) => faq.question && faq.answer);

    if (safeFaqs.length === 0) return null;

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": safeFaqs.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
            }
        }))
    };

    return (
        <div
            className="bg-white rounded-[2rem] border border-slate-200/80 p-6 sm:p-8 md:p-12 shadow-xs"
            itemScope
            itemType="https://schema.org/FAQPage"
        >
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema).replace(/</g, '\\u003c') }}
            />

            {/* Section Header */}
            <div className="mb-8 border-l-4 border-indigo-600 pl-4 sm:pl-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-[11px] font-extrabold uppercase tracking-wider mb-2">
                    <Sparkles size={12} className="text-indigo-600" />
                    <span>Got Questions?</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                    {title}
                </h2>
                {subtitle && (
                    <p className="text-xs sm:text-sm font-medium text-slate-500 mt-1">
                        {subtitle}
                    </p>
                )}
            </div>

            {/* Accordion Stack */}
            <div className="space-y-3">
                {safeFaqs.map((faq, i) => {
                    const isOpen = openIdx === i;
                    const question = faq.question;
                    const answer = faq.answer;

                    return (
                        <div
                            key={faq.id || i}
                            className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                                isOpen
                                    ? 'bg-indigo-50/20 border-indigo-200 shadow-xs'
                                    : 'border-slate-200/80 bg-white hover:border-indigo-100'
                            }`}
                            itemScope
                            itemProp="mainEntity"
                            itemType="https://schema.org/Question"
                        >
                            <button
                                type="button"
                                onClick={() => setOpenIdx(isOpen ? null : i)}
                                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-transparent border-none cursor-pointer group select-none"
                                aria-expanded={isOpen}
                            >
                                <span
                                    className={`text-sm sm:text-base font-bold leading-snug transition-colors duration-200 flex-1 ${
                                        isOpen ? 'text-indigo-600' : 'text-slate-800 group-hover:text-indigo-600'
                                    }`}
                                    itemProp="name"
                                >
                                    {question}
                                </span>

                                <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                                        isOpen
                                            ? 'bg-indigo-600 text-white rotate-180 shadow-xs'
                                            : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-50 group-hover:text-indigo-600'
                                    }`}
                                >
                                    <ChevronDown size={15} />
                                </div>
                            </button>

                            {/* Collapsible Answer */}
                            <div
                                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                                }`}
                                itemScope
                                itemProp="acceptedAnswer"
                                itemType="https://schema.org/Answer"
                            >
                                <div className="overflow-hidden">
                                    <div className="px-5 pb-5 pt-2 text-xs sm:text-sm text-slate-600 border-t border-slate-100 leading-relaxed prose prose-slate max-w-none">
                                        <div
                                            itemProp="text"
                                        >{answer}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FaqAccordionSection;
